import { debounce } from '@theme/maths'

class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super()
    this.sectionId = this.dataset.sectionId
    this.context = this.dataset.context || ''
    this.limit = Number(this.dataset.limit) || 6
    this.types = (this.dataset.types || 'product')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
    this.unavailableProducts = this.dataset.unavailableProducts || ''
    this.input = null
    this.resultsContainer = null
    this.controller = null
    this.debouncedFetch = debounce(this.fetchResults.bind(this), 200)
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="search"]')
    this.resultsContainer =
      this.closest('search')?.querySelector(
        `#PredictiveSearchResults-${this.sectionId}`
      ) ?? null
    if (!(this.input && this.resultsContainer)) return
    this.input.addEventListener('input', this.handleInput.bind(this))
    this.addEventListener('submit', this.handleSubmit.bind(this))
    this.resultsContainer.addEventListener(
      'click',
      this.handleSuggestionClick.bind(this)
    )
  }

  handleInput(event) {
    const query = event.target.value.trim()
    if (!query) {
      this.debouncedFetch.cancel()
      this.clearResults()
      return
    }
    this.debouncedFetch(query)
  }

  handleSubmit(event) {
    event.preventDefault()
    this.searchNow(this.input ? this.input.value : '')
  }

  handleSuggestionClick(event) {
    const suggestion = event.target.closest('[data-search-suggestion]')
    if (!suggestion) return
    event.preventDefault()
    const term = suggestion.dataset.searchSuggestion
    if (this.input) this.input.value = term
    this.searchNow(term)
  }

  searchNow(term) {
    const query = term.trim()
    if (!query) {
      this.debouncedFetch.cancel()
      this.clearResults()
      return
    }
    this.debouncedFetch.cancel()
    this.fetchResults(query)
  }

  async fetchResults(query) {
    if (this.controller) this.controller.abort()
    this.controller = new AbortController()

    const root = window.Shopify?.routes?.root || '/'
    const params = new URLSearchParams({
      q: query,
      'resources[limit_scope]': 'each',
      'resources[limit]': String(this.limit),
      'resources[type]': this.types.join(','),
      section_id: this.sectionId
    })
    if (this.unavailableProducts) {
      params.set(
        'resources[options][unavailable_products]',
        this.unavailableProducts
      )
    }

    try {
      const response = await fetch(`${root}search/suggest?${params}`, {
        signal: this.controller.signal
      })
      if (!response.ok)
        throw new Error(`Predictive search failed: ${response.status}`)

      const text = await response.text()
      const doc = new DOMParser().parseFromString(text, 'text/html')
      const newResults = doc.querySelector(
        `#PredictiveSearchResults-${this.sectionId}`
      )
      if (newResults) {
        this.resultsContainer.innerHTML = newResults.innerHTML
      }
    } catch {}
  }

  clearResults() {
    if (this.resultsContainer) this.resultsContainer.innerHTML = ''
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch, {
    extends: 'form'
  })
}
